/**
 * @file Одна задача скачивания и её состояние в React.
 *
 * Provider живёт над страницами dashboard: переход в другую папку сохраняет
 * передачу, а выход из dashboard отменяет её и освобождает запрос с секретами.
 */
import { useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'

import { DownloadContext } from './DownloadContext'
import {
    checkQuota,
    cleanup,
    fetchRange,
    metadata,
    prepare,
    verify,
} from './api'
import { runDownload } from './engine'
import { asDownloadError } from './model'
import type { DownloadRequest, DownloadState } from './model'
import { openDownloadWriter } from './save'

const ports = {
    open: openDownloadWriter,
    prepare,
    metadata,
    quota: checkQuota,
    fetch: fetchRange,
    verify,
    cleanup,
}

export function DownloadProvider({ children }: { children: ReactNode }) {
    const [state, setState] = useState<DownloadState | null>(null)
    const requestRef = useRef<DownloadRequest | null>(null)
    const active = useRef<{
        controller: AbortController
        // Фаза здесь обновляется сразу: нельзя отменить сборку/сохранение,
        // даже если кнопка на экране ещё не успела скрыться.
        phase: DownloadState['phase']
    } | null>(null)
    const mounted = useRef(false)

    useEffect(() => {
        mounted.current = true

        function beforeUnload(event: BeforeUnloadEvent) {
            if (active.current) event.preventDefault()
        }

        window.addEventListener('beforeunload', beforeUnload)

        return () => {
            mounted.current = false
            window.removeEventListener('beforeunload', beforeUnload)
            active.current?.controller.abort()
            active.current = null
            requestRef.current = null
        }
    }, [])

    function start(request: DownloadRequest): boolean {
        if (active.current || !mounted.current) return false

        // Занимаем слот синхронно, до следующего рендера React.
        const run = {
            controller: new AbortController(),
            phase: 'checking' as DownloadState['phase'],
        }
        active.current = run
        requestRef.current = request
        setState({
            id: crypto.randomUUID(),
            boxId: request.boxId,
            sourcePath: request.path,
            name: request.name,
            kind: request.kind,
            size: null,
            written: 0,
            phase: 'checking',
            error: null,
            cleanupWarning: false,
        })

        function update(patch: Partial<DownloadState>) {
            // Поздние ответы после удаления Provider не обновляют интерфейс.
            if (active.current !== run) return

            setState((current) => current && { ...current, ...patch })
        }

        async function execute() {
            try {
                await runDownload(request, ports, run.controller.signal, {
                    phase: (phase) => {
                        run.phase = phase
                        update({ phase })
                    },
                    metadata: (source, meta) =>
                        update({ name: source.name, size: meta.size }),
                    progress: (written) => update({ written }),
                    cleanupWarning: () => update({ cleanupWarning: true }),
                })
                update({ phase: 'succeeded' })
                if (active.current === run) requestRef.current = null
            } catch (cause) {
                const cancelled = run.controller.signal.aborted
                update({
                    phase: cancelled ? 'cancelled' : 'failed',
                    error: cancelled ? null : asDownloadError(cause).code,
                })
            } finally {
                // Освобождаем слот после закрытия/отмены записи и очистки архива.
                if (active.current === run) active.current = null
            }
        }

        void execute()
        return true
    }

    function cancel() {
        const run = active.current
        if (
            !run ||
            !['preparing', 'checking', 'downloading'].includes(run.phase)
        )
            return

        run.phase = 'cancelling'
        setState((current) => current && { ...current, phase: 'cancelling' })
        run.controller.abort()
    }

    function retry() {
        // Новый файл назначения и нулевой прогресс; докачивания старого файла нет.
        if (requestRef.current) start(requestRef.current)
    }

    function dismiss() {
        if (active.current) return

        requestRef.current = null
        setState(null)
    }

    return (
        <DownloadContext.Provider
            value={{ state, start, cancel, retry, dismiss }}
        >
            {children}
        </DownloadContext.Provider>
    )
}
